import { hoursToMilliseconds } from 'date-fns';

// The WhatsApp customer-service window: a business may send free-form messages only within this many
// hours after the candidate's last inbound message. Shared so backend (getWindowState) and frontend
// (use-whatsapp-chat) compute the window identically.
export const CONVERSATION_WINDOW_HOURS = 24;
export const CONVERSATION_WINDOW_MS = hoursToMilliseconds(CONVERSATION_WINDOW_HOURS);

/* eslint-disable no-magic-numbers */
const MB = 1024 * 1024;

/**
 * Delivery goes through Twilio, so Twilio's own ceiling governs — not Meta's general Cloud API
 * figures, which are higher and would let an oversized file pass validation only to be rejected
 * by Twilio at send time. Per https://www.twilio.com/docs/whatsapp/guidance-whatsapp-media-messages,
 * Twilio caps the overall WhatsApp message at 20 MB, with images specifically capped at 5 MB; it
 * does not publish separate video/audio/document figures, so those fall back to the 20 MB overall
 * cap. These do change over time — re-check that page before adjusting.
 *
 * Shared so backend (WhatsAppMediaService validation) and frontend (the composer's client-side
 * check) enforce the same ceilings — they drifted out of sync once already when duplicated.
 */
export const WHATSAPP_MEDIA_MAX_BYTES = {
  image: 5 * MB,
  video: 20 * MB,
  audio: 20 * MB,
  document: 20 * MB,
} as const;

/**
 * Inbound only: bounds the download of a Twilio-hosted media file, not a validation limit.
 * Inbound media has already cleared Twilio/WhatsApp's own ceiling before it reached us, so
 * this exists purely to cap worker memory on a stalled/oversized response.
 */
export const WHATSAPP_INBOUND_MEDIA_MAX_BYTES = 20 * MB;
/* eslint-enable no-magic-numbers */

export const WHATSAPP_MEDIA_DEFAULT_MAX_BYTES = WHATSAPP_MEDIA_MAX_BYTES.document;

/**
 * Resolves the byte ceiling for a given MIME type, falling back to the document limit
 * for anything that is not an image/video/audio.
 */
export const getWhatsAppMediaMaxBytes = (mimeType?: string | null): number => {
  if (!mimeType) return WHATSAPP_MEDIA_DEFAULT_MAX_BYTES;
  if (mimeType.startsWith('image/')) return WHATSAPP_MEDIA_MAX_BYTES.image;
  if (mimeType.startsWith('video/')) return WHATSAPP_MEDIA_MAX_BYTES.video;
  if (mimeType.startsWith('audio/')) return WHATSAPP_MEDIA_MAX_BYTES.audio;
  return WHATSAPP_MEDIA_MAX_BYTES.document;
};

/**
 * Outbound only: what Twilio accepts for a WhatsApp send, per
 * https://www.twilio.com/docs/whatsapp/guidance-whatsapp-media-messages. Inbound media is stored
 * regardless of type — WhatsApp already constrains what it delivers, and dropping a candidate's
 * file for failing our own list would lose it silently.
 */
export const WHATSAPP_MEDIA_ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'audio/aac',
  'audio/amr',
  'audio/mpeg',
  'audio/mp4',
  'audio/ogg',
  'video/mp4',
  'video/3gpp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
] as const;

export const isWhatsAppMediaMimeTypeAllowed = (mimeType?: string | null): boolean =>
  Boolean(mimeType) && (WHATSAPP_MEDIA_ALLOWED_MIME_TYPES as readonly string[]).includes(mimeType as string);
